/* eslint-disable max-len */
export enum CardType {
  AmEx = 'amex',
  MasterCard = 'mc',
  Visa = 'visa',
  DinersClub = 'diners',
  Discover = 'discover',
  Jcb = 'jcb',
  Maestro = 'maestro',
  Other = 'other',
}

export type CreditCardRefData<T> = { [key: string]: T };

export interface ICardLengthValid {
  isValid(pan: string): boolean;
}

export class ExactCardLength implements ICardLengthValid {
  constructor(private exactCardLength: number) {}
  public isValid(pan: string): boolean {
    return pan.length === this.exactCardLength;
  }
}

export class RangeCardLength implements ICardLengthValid {
  constructor(private minCardLength: number, private maxCardLength: number) {}
  public isValid(pan: string): boolean {
    return pan.length >= this.minCardLength && pan.length <= this.maxCardLength;
  }
}

export interface ICreditCardVendorReference {
  Type: CardType;
  DisplayName: string;
  NumberRegex: RegExp;
  ValidLengths: ICardLengthValid[];
  CvcRegex: RegExp;
  CvcMaxLength: number;
  CvcHintMessage: string;
}

export const CreditCardReferences: CreditCardRefData<ICreditCardVendorReference> =
  {
    [CardType.AmEx]: {
      Type: CardType.AmEx,
      DisplayName: 'American Express',
      NumberRegex: /^(34|37)/,
      ValidLengths: [new ExactCardLength(15)],
      CvcRegex: /^\d{4}$/,
      CvcMaxLength: 4,
      CvcHintMessage:
        'The American Express security code is a 4-digit number printed on the front of your card. It appears above and to the right of your card number.',
    },
    [CardType.MasterCard]: {
      Type: CardType.MasterCard,
      DisplayName: 'MasterCard',
      // 2221-2720, 51-55
      NumberRegex: /^(22(2[1-9]|[3-9][0-9])|2[3-6]|27([0-1][0-9]|20)|5[1-5])/,
      ValidLengths: [new ExactCardLength(16)],
      CvcRegex: /^\d{3}$/,
      CvcMaxLength: 3,
      CvcHintMessage:
        'The verification number for Mastercard is a 3-digit number printed on the back of your card. It is the last 3 digits in the signature strip.',
    },
    [CardType.Visa]: {
      Type: CardType.Visa,
      DisplayName: 'Visa',
      NumberRegex: /^4/,
      ValidLengths: [
        new ExactCardLength(13),
        new ExactCardLength(16),
        new ExactCardLength(19),
      ],
      CvcRegex: /^\d{3}$/,
      CvcMaxLength: 3,
      CvcHintMessage:
        'The verification number for Visa is a 3-digit number printed on the back of your card. It is the last 3 digits in the signature strip.',
    },
    [CardType.DinersClub]: {
      Type: CardType.DinersClub,
      DisplayName: 'Diners Club',
      // 300-305, 3095, 36, 38-39
      NumberRegex: /^(30[0-5]|3095|36|3[8-9])/,
      ValidLengths: [new RangeCardLength(14, 19)],
      CvcRegex: /^\d{3}$/,
      CvcMaxLength: 3,
      CvcHintMessage:
        'The verification number for Diners Club is a 3-digit number printed on the back of your card. It is the last 3 digits in the signature strip.',
    },
    [CardType.Discover]: {
      Type: CardType.Discover,
      DisplayName: 'Discover Card',
      // 6011, 64-65
      NumberRegex: /^(6011|6[4-5])/,
      ValidLengths: [new RangeCardLength(16, 19)],
      CvcRegex: /^\d{3}$/,
      CvcMaxLength: 3,
      CvcHintMessage:
        'The verification number for Discover Card is a 3-digit number printed on the back of your card. It is the last 3 digits in the signature strip.',
    },
    [CardType.Jcb]: {
      Type: CardType.Jcb,
      DisplayName: 'JCB',
      // 3528-3589
      NumberRegex: /^35(2[8-9]|[3-8][0-9])/,
      ValidLengths: [new RangeCardLength(16, 19)],
      CvcRegex: /^\d{3}$/,
      CvcMaxLength: 3,
      CvcHintMessage:
        'The verification number for JCB is a 3-digit number printed on the back of your card. It is the last 3 digits in the signature strip.',
    },
    // Maestro includes Hipercard
    [CardType.Maestro]: {
      Type: CardType.Maestro,
      DisplayName: 'Maestro',
      // 50, 56–58, 6
      NumberRegex: /^(50|5[6-9]|6)/,
      ValidLengths: [new RangeCardLength(12, 19)],
      CvcRegex: /^\d{3}$/,
      CvcMaxLength: 3,
      CvcHintMessage:
        'The verification number for Maestro is a 3-digit number. It is the last 3 digits of your ATM PIN number.',
    },
    [CardType.Other]: {
      Type: CardType.Other,
      DisplayName: '',
      NumberRegex: /a^/, // Nothing
      ValidLengths: [new RangeCardLength(12, 19)],
      CvcRegex: /^\d{3,4}$/,
      CvcMaxLength: 4,
      CvcHintMessage:
        'The verification number for Visa and Mastercard is a 3-digit number printed on the back of your card and for American Express, it is a 4-digit number printed on the front of your card.',
    },
  };
